import { createSelector } from "reselect";
import { selectChannels, selectCurrentChannelId } from ".";
import { ClusterApi } from "@cluster/web-components";
import moment from 'moment'
import { ChannelAlerts } from "../../Channel/ChannelDataTypes";
import { RootState } from "../reducers/persistedReducer";
import { ALERTS_VISIBILITY } from "../reducers/system";

export const selectAlertsVisibility = (state: RootState) => state.system.alertsVisibility;

export const selectAlerts = createSelector(
  selectChannels,
  (channels) => {
    // Flatten alerts from channel alerts and inject channel data into each of them.
    let alerts = channels.reduce((alerts: (ClusterApi.Alert & {id: number})[], channel) => {
      if (channel.alerts.length > 0) {
        for (let alert of channel.alerts) {
          if (moment(alert.expires).isAfter(new Date())) {
            alerts.push({
              id: alert.id,
              message: alert.message,
              notify: false,
              expires: alert.expires,
              cell: alert.cell,
              board: {
                id: channel.id,
                name: channel.name
              }
            });
          }
        }
      }
      return alerts;
    }, []);

    // Sort the alerts newest to oldest
    alerts = alerts.sort((a, b) =>  b.id - a.id);

    return alerts;
  }
)

export const selectAlertsWithVisibility = createSelector(
  selectAlerts,
  selectCurrentChannelId,
  selectAlertsVisibility,
  (alerts, currentChannelId, visibility) => {
    // If channel visibility is turned off, return all alerts.
    if (visibility !== ALERTS_VISIBILITY.CHANNEL) {
      return alerts;
    }

    return alerts.filter(alert => currentChannelId === alert.board.id);
  }
)

export const selectNewestAlert = createSelector(
  selectAlerts,
  (alerts) => {
    return alerts[0]
  }
)

export const selectAlertsPerChannel = createSelector(
  selectChannels,
  (channels) => {
    // Collect alerts from channel list
    return channels.reduce((alertsPerChannel: ChannelAlerts[], channel) => {
      if (channel.alerts.length > 0) {
        let channelAlerts = channel.alerts.filter(channelAlerts => moment(channelAlerts.expires).isAfter(new Date()));
        // Sort the alerts newest to oldest
        channelAlerts = channelAlerts.sort((a, b) =>  b.id - a.id);

        if (channelAlerts.length > 0) {
          alertsPerChannel.push({
            channel: channel.id,
            alerts: channelAlerts
          });
        }
      }
      return alertsPerChannel;
    }, []);
  }
)

export const selectCellHasAlert = createSelector(
  selectAlertsPerChannel,
  (state: RootState, channelId: number, cellId: number) => {return {channelId, cellId}},
  (alertsPerChannel, { channelId, cellId }) => {
    return alertsPerChannel.find(channelAlerts => channelAlerts.channel === channelId)?.alerts.find(alert => alert.cell.id === cellId) ? true : false;
  }
)

export const selectAlertsCount = createSelector(
  selectAlerts,
  (alerts => {
    return alerts.length;
  })
)